import React, { useMemo } from "react";
import _ from "lodash";
import toast from "react-hot-toast";

import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { matchPhrases } from "../../../utils/utilities";
import { getResultActionOptions } from "../../../utils/data";

import Button from "../../themed/button";
import Conditional from "../../common/conditional";
import Input from "../../themed/input/input";
import Select from "../../themed/select/select";

interface PMIResult  extends Record<string, string | number>{
    id: number;
    itemDescription: string;
    identifiedMaterialSpec: string;
    result: string;
}

interface Props {
    initialPMIData?: object;
    onDeleteItem?: () => void;
    elements: string[];
    onSave: (item: PMIResult) => void;
}

const schema = z.object({
    itemDescription: z.string().min(1, { message: 'Item Description cannot be empty' }),
    identifiedMaterialSpec: z.string().min(1, { message: 'Identified Material Spec. cannot be empty' }),
    result: z.string().min(1, { message: 'Result cannot be empty' }),
});

const PMIResultForm: React.FC<Props> = ({ elements, initialPMIData, onDeleteItem, onSave }) => {
    const newSchema = useMemo(() => {
        const elementSchema = elements.reduce((accumulator, currentValue) => {
            accumulator[currentValue] = z.string()
                                            .min(1, { message: `${currentValue} cannot be empty`})
                                            .refine((value) => (!_.isNaN((parseFloat(value))) && _.isNumber(parseFloat(value))) || matchPhrases(value, 'N/A'), { message: `${currentValue} must be a number or 'N/A'` });
            
            return accumulator;
        }, {} as Record<string, any>);

        return schema.extend(elementSchema);
    }, [elements]);

    const { control, formState, reset, handleSubmit } = useForm<PMIResult>({
        resolver: zodResolver(newSchema) as any,
        mode: 'all',
        defaultValues: initialPMIData,
    });

    const onSubmit = (data: PMIResult) => {
        onSave(data);

        if (!initialPMIData) {
            reset();
        }

        toast.success('Saved successfully!');
    };

    return ( 
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
				<h3 className="form-title">PMI Results</h3>

                <div className="container">
                    <div className="row">
                        <div className="col">
                            <Controller
                                name="itemDescription"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        id={field.name}
                                        name={field.name}
                                        value={field.value}
                                        error={formState.errors.itemDescription ? formState.errors.itemDescription.message as string : null}
                                        label="Item Description"
                                        placeholder="Enter Item Description"
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>
                        
                        <div className="col">
                            <Controller
                                name="identifiedMaterialSpec"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        id={field.name}
                                        name={field.name}
                                        value={field.value}
                                        error={formState.errors.identifiedMaterialSpec ? formState.errors.identifiedMaterialSpec.message as string : null}
                                        label="Identified Material Spec"
                                        placeholder="Enter Identified Material Spec"
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>
                        
                        <div className="col">
                            <Controller
                                name="result"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        id={field.name}
                                        name={field.name}
                                        value={field.value}
                                        options={getResultActionOptions()}
                                        error={formState.errors.result ? formState.errors.result.message as string : null}
                                        label="Result"
                                        placeholder="Select Result"
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    
                    <div className="divider my-4"></div>

                     <div className="row">
                        {elements.map((element) => (
                            <div className="col" key={element}>
                                <Controller
                                    name={element}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            id={field.name}
                                            name={field.name}
                                            value={field.value}
                                            error={formState.errors[element] ? formState.errors[element]!.message as string : null}
                                            label={element}
                                            placeholder={"Enter " + element + " Value"}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="divider my-4"></div>

                <div className="d-flex justify-content-end">
                    <Conditional isVisible={!!onDeleteItem}>
                        <div className="me-3">
                            <Button
                                onClick={onDeleteItem} 
                                icon="trash"
                                type="button"
                                label="Delete" 
                                style={{ backgroundColor: 'red' }} 
                            />
                        </div>
                    </Conditional>

                    <Button label="Save PMI Result" disabled={!formState.isValid} type="submit" />
                </div>
			</form>
     );
};
 
export type { PMIResult };
export default PMIResultForm;